import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

import type { HighchartsBaseRef } from "../ui/analytics/charts/HighchartsBase/HighchartsBase";

type ChartExtremes = { min: number; max: number };
type ChartRef = RefObject<HighchartsBaseRef | null>;

type ChartZoomContextValue = {
  register: (ref: ChartRef) => void;
  unregister: (ref: ChartRef) => void;
  applyExtremes: (extremes: { min?: number; max?: number }) => void;
};

const ChartZoomContext = createContext<ChartZoomContextValue | null>(null);

const NOOP_ZOOM: ChartZoomContextValue = {
  register: () => undefined,
  unregister: () => undefined,
  applyExtremes: () => undefined,
};

// Mirrors the follow-up view's syncChartExtremes: zoom a single chart's x-axis.
const setChartExtremes = (ref: ChartRef, { min, max }: ChartExtremes) => {
  const chart = ref.current?.chart;
  if (chart?.xAxis?.[0]) {
    chart.xAxis[0].setExtremes(min, max);
  }
};

/**
 * Registry that lets every graph in a result section self-register its
 * Highcharts ref so a single FilterBar can re-zoom them all at once.
 */
export function ChartZoomProvider({ children }: { children: ReactNode }) {
  const charts = useRef<Set<ChartRef>>(new Set());
  const extremes = useRef<ChartExtremes | null>(null);

  const register = useCallback((ref: ChartRef) => {
    charts.current.add(ref);
    // A chart that mounts after a zoom was set (collapsible section, comparison
    // re-render) should adopt the active window immediately.
    if (extremes.current) setChartExtremes(ref, extremes.current);
  }, []);

  const unregister = useCallback((ref: ChartRef) => {
    charts.current.delete(ref);
  }, []);

  const applyExtremes = useCallback(({ min, max }: { min?: number; max?: number }) => {
    if (typeof min !== "number" || typeof max !== "number") return;
    extremes.current = { min, max };
    charts.current.forEach((ref) => setChartExtremes(ref, { min, max }));
  }, []);

  const value = useMemo(
    () => ({ register, unregister, applyExtremes }),
    [register, unregister, applyExtremes]
  );

  return <ChartZoomContext.Provider value={value}>{children}</ChartZoomContext.Provider>;
}

/**
 * Access the zoom registry to drive it (e.g. from the FilterBar). Returns a
 * no-op outside a provider so callers never need to null-check.
 */
export function useChartZoom(): ChartZoomContextValue {
  return useContext(ChartZoomContext) ?? NOOP_ZOOM;
}

/**
 * Add useZoomChart for shared zoom registration
 *
 * Introduce a chart ref hook that registers charts with ChartZoomProvider when available, while
 * remaining a no-op outside the provider and supporting caller-owned refs.
 */
export function useZoomChart(externalRef?: ChartRef | null): ChartRef {
  const ctx = useContext(ChartZoomContext);
  const localRef = useRef<HighchartsBaseRef | null>(null);
  const ref = externalRef ?? localRef;

  useEffect(() => {
    if (!ctx) return undefined;
    ctx.register(ref);
    return () => ctx.unregister(ref);
  }, [ctx, ref]);

  return ref;
}
